import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { map, publishReplay, refCount } from 'rxjs/operators';

import { Floor, FloorService } from '../floors/services/floor.service';
import { ProductCategory } from '../products/dmo/product-category';
import { ProductsService } from '../products/products.service';
import { RpcService } from '../shared/http/rpc.service';
import { ValuePair } from '../shared/interfaces/ValuePair';
import { ProductVersion } from '../shared/version/product-version';
import { SettingsDefinitionDto } from './dto/setting-definition.dto';
import { SettingsDefinitionRestaurantKitchen } from './dto/settings-definition-all';
import { Device } from './model/device';
import { DeviceProfile } from './model/device-profile.model';
import { moduleId } from './model/module-id';
import { SettingsDefinition } from './model/settings-definition/settings-definition.model';
import { DeviceProfileDto } from './dto/device-profile.dto';
import { TokenService } from '../authentication/token.service';

@Injectable()
export class DeviceManagementService {
  public outDatedDevices: Array<Device> = [];
  public profiles$: Observable<Array<DeviceProfile>>;
  public devices$: Observable<Array<Device>>;

  private _devices: Array<Device>;
  private readonly uri: string = '/ios/versions/version.json';
  private settingsDefinition: SettingsDefinitionRestaurantKitchen = {
    restaurant: null,
    kitchen: null
  };

  constructor(
    private rpc: RpcService,
    private productsService: ProductsService,
    private http: HttpClient,
    private tokenService: TokenService,
    private floorService: FloorService
  ) {
    this.tokenService.tokenChanged$.subscribe(() => {
      this.profiles$ = null;
      this.devices$ = null;
      this.outDatedDevices = [];
    });
    this.settingsDefinition = {
      restaurant: null,
      kitchen: null
    };
  }

  public getDevices(forceReload: boolean = false): Observable<Array<Device>> {
    if (!this.devices$ || forceReload) {
      this.devices$ = this.rpc.makeManagerCall('getDevices').pipe(
        map((devices: [Object]) => {
          return devices.map((device: any) => {
            return new Device(
              device.oid,
              device.applicationId,
              device.applicationVersion
            );
          });
        }),
        publishReplay(1),
        refCount()
      );
    }
    return this.devices$;
  }

  public getOutdatedDevices(forceReload: boolean = false): Observable<number> {
    // tslint:disable-next-line
    return combineLatest(
      this.getDevices(forceReload),
      this.getVersions(),
      (devices: Array<Device>, productVersions: Array<ProductVersion>) => {
        this.outDatedDevices = devices.filter((device: Device) => {
          const outdatedDeviceVersion: ProductVersion = productVersions.find(
            (version: ProductVersion) => {
              return version.name === device.applicationId;
            }
          );
          if (outdatedDeviceVersion) {
            return !device.version.isGreaterOrEqualThen(
              outdatedDeviceVersion.version
            );
          } else {
            return false;
          }
        });

        return this.outDatedDevices.length;
      }
    );
  }

  public getProfiles(
    forceReload: boolean = false
  ): Observable<Array<DeviceProfile>> {
    if (!this.profiles$ || forceReload) {
      this.profiles$ = this.rpc
        .makeManagerCall('getDeviceProfiles', [
          [moduleId.RESTAURANT, moduleId.MENU, moduleId.CFD, moduleId.KITCHEN]
        ])
        .pipe(
          map((profiles: Array<DeviceProfileDto>) =>
            profiles.map(DeviceProfile.fromDTO)
          ),
          publishReplay(1),
          refCount()
        );
    }
    return this.profiles$;
  }

  public getProfilesForApplication(
    forceReload: boolean = false,
    forModuleId: string
  ): Observable<Array<DeviceProfile>> {
    return this.getProfiles(forceReload).pipe(
      map((deviceProfiles: Array<DeviceProfile>) => {
        return deviceProfiles.filter((deviceProfile: DeviceProfile) => {
          return deviceProfile.moduleName === forModuleId;
        });
      }),
      publishReplay(1),
      refCount()
    );
  }

  public getProfileById(
    forceReload: boolean = false,
    forModuleId: string,
    id: number
  ): Observable<DeviceProfile> {
    return this.getProfilesForApplication(forceReload, forModuleId).pipe(
      map((profiles: Array<DeviceProfile>) => {
        return profiles.find((profile: DeviceProfile) => {
          return profile.id === id;
        });
      })
    );
  }

  public getMostRecentSettingsDefinition(
    forceReload: boolean = false,
    applicationId: string = 'restaurant'
  ): Observable<SettingsDefinition> {
    if (!this.settingsDefinition[applicationId] || forceReload) {
      this.settingsDefinition[applicationId] = this.rpc
        .makeManagerCall('getMostRecentSettingsDefinition', [applicationId])
        .pipe(
          map((settingsDefinitionDto: SettingsDefinitionDto) => {
            if (applicationId === moduleId.KITCHEN) {
              const defaultSettings: SettingsDefinitionDto = SettingsDefinition.getDefaultKitchenSettings();

              if (
                !settingsDefinitionDto ||
                defaultSettings.settingsDefinitionVersion >
                  settingsDefinitionDto.settingsDefinitionVersion
              ) {
                settingsDefinitionDto = defaultSettings;
                this.saveSettingsDefinition(settingsDefinitionDto).subscribe();
              }
            }

            return SettingsDefinition.fromDTO(
              settingsDefinitionDto,
              this.asyncMapping()
            );
          }),
          publishReplay(1),
          refCount()
        );
    }
    return this.settingsDefinition[applicationId];
  }

  public saveDeviceProfile(deviceProfile: DeviceProfile): Observable<any> {
    const deviceProfileDto: any = deviceProfile.toDTO(deviceProfile);
    return this.rpc.makeManagerCall('saveDeviceProfile', [
      _.omit(deviceProfileDto, ['settings']),
      deviceProfileDto.settings,
      [],
      []
    ]);
  }

  public saveSettingsDefinition(
    settingsDefinitionDto: SettingsDefinitionDto
  ): Observable<any> {
    return this.rpc.makeManagerCall('saveSettingsDefinition', [
      settingsDefinitionDto.settingsDefinitionJson,
      moduleId.KITCHEN
    ]);
  }

  public deleteDeviceProfile(deviceProfile: DeviceProfile): Observable<any> {
    return this.rpc.makeManagerCall('deleteDeviceProfile', [deviceProfile.id]);
  }

  public get devices(): Array<Device> {
    return this._devices;
  }

  public set devices(devices: Array<Device>) {
    this._devices = devices;
  }

  private getVersions(): Observable<Array<ProductVersion>> {
    return this.http.get(this.uri).pipe(
      map((response: { outdatedVersion: ProductVersion }) => {
        const outdatedVersions: ProductVersion = response.outdatedVersion;
        const applicationVersions: Array<ProductVersion> = [];

        Object.keys(outdatedVersions).forEach((application: string) => {
          applicationVersions.push(
            new ProductVersion(application, outdatedVersions[application])
          );
        });

        return applicationVersions;
      })
    );
  }

  private asyncMapping(): { [key: string]: Function } {
    const mapping: { [key: string]: Function } = {};
    mapping['productCategories'] = (): Observable<Array<ValuePair>> => {
      return this.productsService.getProductCategories().pipe(
        map((productCategories: Array<ProductCategory>) => {
          return productCategories.map((productCategory: ProductCategory) => {
            return {
              name: productCategory.name,
              value: productCategory.id
            };
          });
        })
      );
    };
    mapping['floors'] = (): Observable<Array<ValuePair>> => {
      return this.floorService.getFloors().pipe(
        map((floors: Array<Floor>) => {
          return floors.map((floor: Floor) => {
            return {
              name: floor.name,
              value: floor.oid
            };
          });
        })
      );
    };
    return mapping;
  }
}
